<!-- ============================================================
     COMMUNICATIONS HUB
     Circulars · Events · Announcements · Meetings
     Counselling · Complaints · Reminders
     ============================================================ -->
<div class="comm-page">

  @if (toast()) {
    <div class="comm-toast alert alert-success d-flex align-items-center gap-2 shadow">
      <i class="bi bi-check-circle-fill fs-5"></i>
      <strong>{{ toast() }}</strong>
    </div>
  }

  <!-- ══════════════════════════════════════════════════════
       ADD / EDIT MODAL
       ══════════════════════════════════════════════════════ -->
  @if (showForm()) {
    <div class="comm-overlay" (click)="closeForm()">
      <div class="comm-modal" (click)="$event.stopPropagation()">
        @let k = form.kind ?? 'circular';
        <div class="cmh" [style.background]="kinds[k].gradient">
          <div class="cmh-icon"><i class="bi {{ kinds[k].icon }}"></i></div>
          <div>
            <h5 class="cmh-title">{{ isEditing() ? 'Edit' : 'Add' }} {{ kinds[k].label }}</h5>
            <p class="cmh-sub">{{ isEditing() ? 'Update the details below' : 'Fill the required fields and save' }}</p>
          </div>
          <button class="cmh-close" type="button" (click)="closeForm()"><i class="bi bi-x-lg"></i></button>
        </div>
        <div class="cmb">
          <div class="row g-3">
            @for (f of visibleFormFields(); track f.key) {
              <div [class]="f.col">
                <label class="form-label fw-semibold">
                  {{ f.label }}
                  @if (f.required) { <span class="req">*</span> }
                </label>

                @if (f.type === 'text') {
                  <input type="text" class="form-control" [name]="f.key"
                    [(ngModel)]="form[f.key]" (ngModelChange)="touchForm()"
                    [placeholder]="f.placeholder ?? f.label" maxlength="200"
                    [class.is-invalid]="formSubmitted && formErrors[f.key]">
                }
                @if (f.type === 'number') {
                  <input type="number" class="form-control" [name]="f.key"
                    [(ngModel)]="form[f.key]" (ngModelChange)="touchForm()" min="0"
                    [class.is-invalid]="formSubmitted && formErrors[f.key]">
                }
                @if (f.type === 'date') {
                  <input type="date" class="form-control" [name]="f.key"
                    [(ngModel)]="form[f.key]" (ngModelChange)="touchForm()"
                    [class.is-invalid]="formSubmitted && formErrors[f.key]">
                }
                @if (f.type === 'time') {
                  <input type="time" class="form-control" [name]="f.key"
                    [(ngModel)]="form[f.key]" (ngModelChange)="touchForm()"
                    [class.is-invalid]="formSubmitted && formErrors[f.key]">
                }
                @if (f.type === 'select') {
                  <select class="form-select" [name]="f.key"
                    [(ngModel)]="form[f.key]" (ngModelChange)="touchForm()"
                    [class.is-invalid]="formSubmitted && formErrors[f.key]">
                    <option value="">— Select —</option>
                    @for (opt of f.options ?? []; track opt) {
                      <option [value]="opt">{{ opt }}</option>
                    }
                  </select>
                }
                @if (f.type === 'textarea') {
                  <textarea class="form-control" [name]="f.key" rows="3"
                    [(ngModel)]="form[f.key]" (ngModelChange)="touchForm()"
                    [placeholder]="f.placeholder ?? f.label" maxlength="800"
                    [class.is-invalid]="formSubmitted && formErrors[f.key]"></textarea>
                }

                @if (formSubmitted && formErrors[f.key]) {
                  <div class="invalid-feedback d-block">{{ formErrors[f.key] }}</div>
                }
              </div>
            }
          </div>
        </div>
        <div class="cmf">
          <button class="btn btn-outline-secondary px-4" type="button" (click)="closeForm()">Cancel</button>
          <button class="btn comm-btn-primary px-5" type="button" (click)="save()">
            <i class="bi bi-check-circle-fill me-1"></i>{{ isEditing() ? 'Update' : 'Save' }}
          </button>
        </div>
      </div>
    </div>
  }

  <!-- ══════════════════════════════════════════════════════
       DETAIL MODAL
       ══════════════════════════════════════════════════════ -->
  @if (showDetail() && viewing(); as v) {
    <div class="comm-overlay" (click)="showDetail.set(false)">
      <div class="comm-modal comm-modal-detail" (click)="$event.stopPropagation()">
        <div class="cmh" [style.background]="kinds[v.kind].gradient">
          <div class="cmh-icon"><i class="bi {{ kinds[v.kind].icon }}"></i></div>
          <div>
            <h5 class="cmh-title">{{ v.title }}</h5>
            <p class="cmh-sub">{{ v.ref_no }} · {{ kinds[v.kind].label }}</p>
          </div>
          <button class="cmh-close" type="button" (click)="showDetail.set(false)"><i class="bi bi-x-lg"></i></button>
        </div>
        <div class="cmb">
          <div class="d-flex flex-wrap gap-2 mb-3">
            <span class="st-pill" [ngClass]="statusClass(v.status)">{{ v.status }}</span>
            @if (v.priority) {
              <span class="st-pill" [ngClass]="priorityClass(v.priority)">{{ v.priority }}</span>
            }
            @if (v.category) {
              <span class="cat-chip" [ngClass]="categoryClass(v.category)">{{ v.category }}</span>
            }
          </div>
          <div class="kv-grid">
            <div class="kv-item"><span>Date</span><span>{{ formatDate(v.date) }}</span></div>
            @if (v.end_date) { <div class="kv-item"><span>End / Expiry</span><span>{{ formatDate(v.end_date) }}</span></div> }
            @if (v.start_time) { <div class="kv-item"><span>Time</span><span>{{ formatTime(v.start_time) }}{{ v.end_time ? ' – ' + formatTime(v.end_time) : '' }}</span></div> }
            @if (v.venue) { <div class="kv-item"><span>Venue</span><span>{{ v.venue }}</span></div> }
            @if (v.issued_by) { <div class="kv-item"><span>Owner</span><span>{{ v.issued_by }}</span></div> }
            @if (v.audience) { <div class="kv-item"><span>Audience</span><span>{{ v.audience }}{{ v.class_name ? ' · ' + v.class_name : '' }}</span></div> }
            @if (v.person_name) { <div class="kv-item"><span>Person</span><span>{{ v.person_name }}{{ v.person_role ? ' (' + v.person_role + ')' : '' }}</span></div> }
            @if (v.related_to) { <div class="kv-item"><span>Related</span><span>{{ v.related_to }}</span></div> }
            @if (v.follow_up) { <div class="kv-item"><span>Follow-up</span><span>{{ formatDate(v.follow_up) }}</span></div> }
            @if (v.duration_mins) { <div class="kv-item"><span>Duration</span><span>{{ v.duration_mins }} mins</span></div> }
            @if (v.attachment) { <div class="kv-item"><span>Attachment</span><span>{{ v.attachment }}</span></div> }
          </div>
          @if (v.description) {
            <div class="detail-block">
              <div class="detail-label">Details</div>
              <p>{{ v.description }}</p>
            </div>
          }
          @if (v.notes) {
            <div class="detail-block">
              <div class="detail-label">Notes / Resolution / Minutes</div>
              <p>{{ v.notes }}</p>
            </div>
          }
          @if (quickStatuses(v).length) {
            <div class="quick-status">
              <span class="detail-label mb-2 d-block">Update status</span>
              <div class="d-flex flex-wrap gap-2">
                @for (s of quickStatuses(v); track s) {
                  <button type="button" class="btn btn-sm btn-outline-secondary" (click)="setStatus(v, s)">{{ s }}</button>
                }
              </div>
            </div>
          }
        </div>
        <div class="cmf">
          <button class="btn btn-outline-danger me-auto" type="button" (click)="confirmDelete(v)">
            <i class="bi bi-trash me-1"></i>Delete
          </button>
          <button class="btn btn-outline-secondary" type="button" (click)="showDetail.set(false)">Close</button>
          <button class="btn comm-btn-primary" type="button" (click)="openEdit(v)">
            <i class="bi bi-pencil-fill me-1"></i>Edit
          </button>
        </div>
      </div>
    </div>
  }

  <!-- ══════════════════════════════════════════════════════
       DELETE CONFIRM
       ══════════════════════════════════════════════════════ -->
  @if (showDelete() && deleteTarget(); as t) {
    <div class="comm-overlay" (click)="cancelDelete()">
      <div class="comm-modal comm-modal-sm" (click)="$event.stopPropagation()">
        <div class="cmh cmh-danger">
          <div class="cmh-icon"><i class="bi bi-trash-fill"></i></div>
          <div>
            <h5 class="cmh-title">Delete {{ kinds[t.kind].label }}</h5>
            <p class="cmh-sub">This cannot be undone</p>
          </div>
          <button class="cmh-close" type="button" (click)="cancelDelete()"><i class="bi bi-x-lg"></i></button>
        </div>
        <div class="cmb">
          <p class="mb-0">Delete <strong>{{ t.title }}</strong> ({{ t.ref_no }})?</p>
        </div>
        <div class="cmf">
          <button class="btn btn-outline-secondary px-4" type="button" (click)="cancelDelete()">Cancel</button>
          <button class="btn btn-danger px-4" type="button" (click)="executeDelete()">
            <i class="bi bi-trash-fill me-1"></i>Delete
          </button>
        </div>
      </div>
    </div>
  }

  <!-- ══════════════════════════════════════════════════════
       PAGE HEADER
       ══════════════════════════════════════════════════════ -->
  <div class="comm-ph mb-4">
    <div class="cph-left">
      <div class="comm-logo"><i class="bi bi-megaphone-fill"></i></div>
      <div>
        <h1 class="comm-title">Communications</h1>
        <p class="comm-subtitle">Circulars, events, announcements, meetings, counselling, complaints &amp; reminders</p>
      </div>
    </div>
    @if (listKind(); as lk) {
      <button class="btn comm-btn-primary" type="button" (click)="openAdd(lk)">
        <i class="bi bi-plus-circle-fill me-1"></i>{{ kinds[lk].addLabel }}
      </button>
    }
  </div>

  <!-- TABS -->
  <div class="comm-tabs mb-4">
    @for (t of tabs; track t.key) {
      <button class="ctab" type="button" [class.active]="activeTab() === t.key" (click)="setTab(t.key)">
        <i class="bi {{ t.icon }} me-1"></i>{{ t.label }}
        @if (tabCount(t.key) > 0) {
          <span class="ctab-badge">{{ tabCount(t.key) }}</span>
        }
      </button>
    }
  </div>

  <!-- ══════════════════════════════════════════════════════
       OVERVIEW
       ══════════════════════════════════════════════════════ -->
  @if (activeTab() === 'overview') {
    <div class="comm-kpi-row mb-4">
      <button class="kpi-card kpi-cir" type="button" (click)="setTab('circular')">
        <div class="kpi-icon"><i class="bi bi-file-earmark-text-fill"></i></div>
        <div class="kpi-body">
          <div class="kpi-val">{{ stats().circulars }}</div>
          <div class="kpi-label">Circulars</div>
          <div class="kpi-sub">{{ stats().publishedCirculars }} published</div>
        </div>
      </button>
      <button class="kpi-card kpi-evt" type="button" (click)="setTab('event')">
        <div class="kpi-icon"><i class="bi bi-calendar-event-fill"></i></div>
        <div class="kpi-body">
          <div class="kpi-val">{{ stats().upcomingEvents }}</div>
          <div class="kpi-label">Upcoming Events</div>
          <div class="kpi-sub">{{ stats().events }} total</div>
        </div>
      </button>
      <button class="kpi-card kpi-ann" type="button" (click)="setTab('announcement')">
        <div class="kpi-icon"><i class="bi bi-megaphone-fill"></i></div>
        <div class="kpi-body">
          <div class="kpi-val">{{ stats().announcements }}</div>
          <div class="kpi-label">Active Notices</div>
          <div class="kpi-sub">Announcements live</div>
        </div>
      </button>
      <button class="kpi-card kpi-mtg" type="button" (click)="setTab('meeting')">
        <div class="kpi-icon"><i class="bi bi-people-fill"></i></div>
        <div class="kpi-body">
          <div class="kpi-val">{{ stats().meetings }}</div>
          <div class="kpi-label">Meetings</div>
          <div class="kpi-sub">Scheduled</div>
        </div>
      </button>
      <button class="kpi-card kpi-csn" type="button" (click)="setTab('counselling')">
        <div class="kpi-icon"><i class="bi bi-heart-pulse-fill"></i></div>
        <div class="kpi-body">
          <div class="kpi-val">{{ stats().counselling }}</div>
          <div class="kpi-label">Counselling</div>
          <div class="kpi-sub">Scheduled / follow-up</div>
        </div>
      </button>
      <button class="kpi-card kpi-cmp" type="button" [class.kpi-alert]="stats().openComplaints > 0" (click)="setTab('complaint')">
        <div class="kpi-icon"><i class="bi bi-exclamation-octagon-fill"></i></div>
        <div class="kpi-body">
          <div class="kpi-val">{{ stats().openComplaints }}</div>
          <div class="kpi-label">Open Complaints</div>
          <div class="kpi-sub">Need action</div>
        </div>
      </button>
      <button class="kpi-card kpi-rem" type="button" [class.kpi-alert]="stats().overdueReminders > 0" (click)="setTab('reminder')">
        <div class="kpi-icon"><i class="bi bi-alarm-fill"></i></div>
        <div class="kpi-body">
          <div class="kpi-val">{{ stats().overdueReminders }}</div>
          <div class="kpi-label">Overdue</div>
          <div class="kpi-sub">{{ stats().pendingReminders }} pending</div>
        </div>
      </button>
    </div>

    <div class="row g-4 mb-2">
      <div class="col-lg-6">
        <div class="comm-card h-100">
          <div class="ccard-hdr"><i class="bi bi-calendar-week me-2"></i>Upcoming schedule</div>
          <div class="ccard-body p-0">
            @if (upcomingFeed().length === 0) {
              <div class="empty-st"><i class="bi bi-calendar-x"></i><p>Nothing scheduled</p></div>
            } @else {
              @for (item of upcomingFeed(); track item.id) {
                <button class="feed-row" type="button" (click)="openDetail(item)">
                  <div class="feed-icon" [style.background]="kinds[item.kind].gradient">
                    <i class="bi {{ kinds[item.kind].icon }}"></i>
                  </div>
                  <div class="feed-info">
                    <div class="feed-title">{{ item.title }}</div>
                    <div class="feed-meta">
                      {{ kinds[item.kind].label }} · {{ formatDate(item.date) }}
                      @if (item.start_time) { · {{ formatTime(item.start_time) }} }
                      @if (item.venue) { · {{ item.venue }} }
                    </div>
                  </div>
                  <span class="st-pill" [ngClass]="statusClass(item.status)">{{ item.status }}</span>
                </button>
              }
            }
          </div>
        </div>
      </div>
      <div class="col-lg-6">
        <div class="comm-card h-100">
          <div class="ccard-hdr alert-hdr"><i class="bi bi-exclamation-triangle-fill me-2"></i>Needs attention</div>
          <div class="ccard-body p-0">
            @if (attentionItems().length === 0) {
              <div class="empty-st"><i class="bi bi-check2-circle"></i><p>All clear</p></div>
            } @else {
              @for (item of attentionItems(); track item.id) {
                <button class="feed-row" type="button" (click)="openDetail(item)">
                  <div class="feed-icon" [style.background]="kinds[item.kind].gradient">
                    <i class="bi {{ kinds[item.kind].icon }}"></i>
                  </div>
                  <div class="feed-info">
                    <div class="feed-title">{{ item.title }}</div>
                    <div class="feed-meta">{{ item.ref_no }} · {{ formatDate(item.date) }}</div>
                  </div>
                  <span class="st-pill" [ngClass]="statusClass(item.status)">{{ item.status }}</span>
                </button>
              }
            }
          </div>
        </div>
      </div>
    </div>

    <div class="comm-card mt-4">
      <div class="ccard-hdr"><i class="bi bi-file-earmark-text-fill me-2"></i>Recent circulars</div>
      <div class="ccard-body p-0">
        @for (c of recentCirculars(); track c.id) {
          <button class="feed-row" type="button" (click)="setTab('circular'); openDetail(c)">
            <div class="feed-icon" [style.background]="kinds.circular.gradient">
              <i class="bi bi-file-earmark-text-fill"></i>
            </div>
            <div class="feed-info">
              <div class="feed-title">{{ c.title }}</div>
              <div class="feed-meta">{{ c.ref_no }} · {{ formatDate(c.date) }} · {{ c.issued_by }}</div>
            </div>
            <span class="st-pill" [ngClass]="statusClass(c.status)">{{ c.status }}</span>
          </button>
        }
      </div>
    </div>
  }

  <!-- ══════════════════════════════════════════════════════
       LIST (per kind)
       ══════════════════════════════════════════════════════ -->
  @if (meta(); as m) {
    <div class="comm-card">
      <div class="filter-bar">
        <div class="search-wrap">
          <i class="bi bi-search"></i>
          <input type="search" class="form-control" [placeholder]="m.searchPlaceholder"
            [ngModel]="search()" (ngModelChange)="onSearch($event)" autocomplete="off">
          @if (search()) {
            <button class="search-clear" type="button" (click)="onSearch('')"><i class="bi bi-x-circle-fill"></i></button>
          }
        </div>
        <select class="form-select filt" [ngModel]="statusFilter()" (ngModelChange)="statusFilter.set($event); currentPage.set(1)">
          <option value="">All statuses</option>
          @for (s of m.statuses; track s) { <option [value]="s">{{ s }}</option> }
        </select>
        <select class="form-select filt" [ngModel]="categoryFilter()" (ngModelChange)="categoryFilter.set($event); currentPage.set(1)">
          <option value="">All {{ m.key === 'event' || m.key === 'meeting' || m.key === 'counselling' ? 'types' : m.key === 'reminder' ? 'repeats' : 'categories' }}</option>
          @for (c of m.categories; track c) { <option [value]="c">{{ c }}</option> }
        </select>
        @if (m.usePriority) {
          <select class="form-select filt" [ngModel]="priorityFilter()" (ngModelChange)="priorityFilter.set($event); currentPage.set(1)">
            <option value="">All priorities</option>
            @for (p of ['Low','Normal','High','Urgent']; track p) { <option [value]="p">{{ p }}</option> }
          </select>
        }
        <input type="date" class="form-control filt-date" title="From date"
          [ngModel]="dateFrom()" (ngModelChange)="dateFrom.set($event); currentPage.set(1)">
        <input type="date" class="form-control filt-date" title="To date"
          [ngModel]="dateTo()" (ngModelChange)="dateTo.set($event); currentPage.set(1)">
        @if (hasActiveFilters) {
          <button class="btn btn-outline-secondary btn-sm" type="button" (click)="resetFilters()">
            <i class="bi bi-arrow-counterclockwise me-1"></i>Reset
          </button>
        }
        <div class="ms-auto result-count">{{ filtered().length }} result{{ filtered().length === 1 ? '' : 's' }}</div>
      </div>

      @if (paged().length === 0) {
        <div class="empty-st">
          <i class="bi {{ m.icon }}"></i>
          <p>No {{ m.plural.toLowerCase() }} match the current search or filters.</p>
          <button class="btn comm-btn-primary btn-sm" type="button" (click)="openAdd(m.key)">
            <i class="bi bi-plus-circle me-1"></i>{{ m.addLabel }}
          </button>
        </div>
      } @else {
        <div class="table-responsive">
          <table class="comm-table">
            <thead>
              <tr>
                @for (col of m.columns; track col.key) {
                  <th class="sortable" (click)="sort(col.key)">
                    {{ col.label }}
                    <i class="bi {{ sortIcon(col.key) }}"></i>
                  </th>
                }
                <th class="th-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              @for (row of paged(); track row.id) {
                <tr>
                  @for (col of m.columns; track col.key) {
                    <td>
                      @if (col.type === 'badge') {
                        <span class="st-pill" [ngClass]="col.key === 'status' ? statusClass(effectiveStatus(row)) : categoryClass(row.category)">
                          {{ col.key === 'status' ? effectiveStatus(row) : displayCell(row, col) }}
                        </span>
                      } @else if (col.type === 'priority') {
                        <span class="st-pill" [ngClass]="priorityClass(row.priority)">{{ row.priority || '—' }}</span>
                      } @else if (col.key === 'title' || col.key === 'person_name') {
                        <button class="link-btn" type="button" (click)="openDetail(row)">{{ displayCell(row, col) }}</button>
                        @if (col.key === 'title') {
                          <div class="tiny-meta">{{ row.ref_no }}</div>
                        }
                      } @else {
                        <span class="cell-val">{{ displayCell(row, col) }}</span>
                      }
                    </td>
                  }
                  <td class="td-actions">
                    <div class="d-flex gap-1">
                      <button class="act-btn view" type="button" title="View" (click)="openDetail(row)"><i class="bi bi-eye"></i></button>
                      <button class="act-btn edit" type="button" title="Edit" (click)="openEdit(row)"><i class="bi bi-pencil"></i></button>
                      @if (row.kind === 'reminder' && effectiveStatus(row) !== 'Done') {
                        <button class="act-btn done" type="button" title="Mark done" (click)="setStatus(row, 'Done')"><i class="bi bi-check2"></i></button>
                      }
                      @if (row.kind === 'circular' && row.status === 'Draft') {
                        <button class="act-btn done" type="button" title="Publish" (click)="setStatus(row, 'Published')"><i class="bi bi-send"></i></button>
                      }
                      <button class="act-btn del" type="button" title="Delete" (click)="confirmDelete(row)"><i class="bi bi-trash"></i></button>
                    </div>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>

        @if (totalPages() > 1) {
          <div class="pager">
            <button class="pg-btn" type="button" [disabled]="currentPage() === 1" (click)="goToPage(currentPage() - 1)">
              <i class="bi bi-chevron-left"></i>
            </button>
            @for (p of pageNumbers; track p) {
              <button class="pg-btn" type="button" [class.active]="currentPage() === p" (click)="goToPage(p)">{{ p }}</button>
            }
            <button class="pg-btn" type="button" [disabled]="currentPage() === totalPages()" (click)="goToPage(currentPage() + 1)">
              <i class="bi bi-chevron-right"></i>
            </button>
            <span class="pg-info">Page {{ currentPage() }} of {{ totalPages() }}</span>
          </div>
        }
      }
    </div>
  }
</div>
